/**
 * Distinguish real auth failures from transient network/server errors.
 * Prevents clearing a valid token when the API is briefly unreachable.
 */
export function isDefinitiveAuthFailure(error) {
    const status = error?.response?.status;
    const message = String(error?.response?.data?.message || '');

    if (status === 401) {
        return (
            message.includes('Session expired') ||
            message.includes('Invalid session') ||
            message.includes('Token expired') ||
            message.includes('Invalid token') ||
            message.includes('Please login again') ||
            message.includes('Admin not found') ||
            message.includes('Token organization mismatch') ||
            message.includes('2FA verification required')
        );
    }

    if (status === 403) {
        return (
            message.includes('Account is not active') ||
            message.includes('Account is locked') ||
            message.includes('Access denied from this IP address')
        );
    }

    return false;
}

export function isTransientNetworkError(error) {
    return !error?.response && Boolean(error?.request);
}

/**
 * 429 (rate limited) and 5xx mean "ask again later", not "your session is gone".
 * Treating them as auth failures signs a valid admin out of the dashboard.
 */
export function isRetryableServerError(error) {
    const status = error?.response?.status;
    if (!Number.isFinite(status)) return false;
    return status === 429 || (status >= 500 && status <= 599);
}

/**
 * Retry-After is sent in seconds; clamp it so a 15-minute window never stalls the UI.
 * Accepts either an axios error or a bare axios response.
 */
export function getRetryAfterMs(errorOrResponse, fallbackMs, maxMs = 5000) {
    const headers =
        errorOrResponse?.response?.headers ?? errorOrResponse?.headers ?? {};
    const seconds = Number.parseInt(headers['retry-after'], 10);
    if (Number.isFinite(seconds) && seconds > 0) {
        return Math.min(seconds * 1000, maxMs);
    }
    return Math.min(fallbackMs, maxMs);
}

const GOOGLE_AUTH_ERROR_KEYS = {
    GOOGLE_NO_ACCOUNT: 'auth.google.errors.noAccount',
    GOOGLE_ACCOUNT_EXISTS: 'auth.google.errors.accountExists',
};

/** Message for a failed Google OAuth redirect (`?error=…&errorCode=…`), translated when the code is known. */
export function getGoogleAuthRedirectError(searchParams, t) {
    const message = searchParams.get('error');
    if (!message) return '';
    const key = GOOGLE_AUTH_ERROR_KEYS[searchParams.get('errorCode')];
    return key ? t(key) : message;
}
